import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Download, FileUp, Loader2, MessageSquareText, Pencil, Plus, Trash2, Zap } from 'lucide-react'
import { EmptyState, ErrorState, Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { SelectableTable, type TableColumn } from '@/pages/integrations/accounts-table'
import {
  useBulkDeleteQuickReplies,
  useDeleteQuickReply,
  useQuickReplies,
  type QuickReply,
} from '@/hooks/use-quick-replies'
import { downloadCsv, serializeQuickRepliesCsv } from './quick-reply-csv'
import { QuickReplyFormDialog } from './quick-reply-form-dialog'
import { QuickReplyImportDialog } from './quick-reply-import-dialog'

/** Giá trị lọc đặc biệt: tất cả / chưa có danh mục. */
const ALL = '__all__'
const NONE = '__none__'

function exportCsv(items: QuickReply[]) {
  downloadCsv(
    `tra-loi-nhanh-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
    serializeQuickRepliesCsv(items.map(({ title, content, category }) => ({ title, content, category }))),
  )
}

/**
 * Popup quản lý Trả lời nhanh — CHỈ mở từ màn Hội thoại: nút ⚡ trong ô soạn tin → "Quản lý thư viện", hoặc đường dẫn riêng
 * /conversations/quick-replies (quick-replies-route.tsx) để phân quyền / gửi link. Không còn là tab của Khách hàng.
 * Thư viện kịch bản dùng chung cả công ty + mẫu cá nhân. Quyền (backend `quick_replies.*`): mẫu cá nhân ai dùng được cũng tự quản lý;
 * mẫu dùng chung theo cờ `can` (thêm / sửa / xoá / nhập) máy chủ trả về.
 */
export function QuickRepliesDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(88vh,820px)] max-w-5xl flex-col gap-0 overflow-hidden p-0">
        {open && <QuickRepliesManager />}
      </DialogContent>
    </Dialog>
  )
}

function QuickRepliesManager() {
  const { t } = useTranslation('quickReplies')
  const { data, isLoading, isError, error } = useQuickReplies()
  const del = useDeleteQuickReply()
  const bulkDel = useBulkDeleteQuickReplies()
  const [category, setCategory] = useState(ALL)
  const [form, setForm] = useState<{ open: boolean; item: QuickReply | null }>({ open: false, item: null })
  const [importOpen, setImportOpen] = useState(false)
  const [confirm, setConfirm] = useState<{ items: QuickReply[]; done?: () => void } | null>(null)

  const items = useMemo(() => data?.items ?? [], [data])
  const categories = useMemo(() => data?.categories ?? [], [data])
  const can = data?.can ?? { create: !!data?.canManageTeam, update: false, delete: false, import: false }
  const canEdit = (q: QuickReply) => q.isPersonal || can.update
  const canDelete = (q: QuickReply) => q.isPersonal || can.delete

  const counts = useMemo(() => {
    const m = new Map<string, number>()
    for (const q of items) {
      const k = q.category || NONE
      m.set(k, (m.get(k) ?? 0) + 1)
    }
    return m
  }, [items])

  const shown = useMemo(
    () =>
      category === ALL
        ? items
        : items.filter((q) => (category === NONE ? !q.category : q.category === category)),
    [items, category],
  )

  async function runDelete() {
    if (!confirm) return
    const { items: targets, done } = confirm
    try {
      if (targets.length === 1) {
        await del.mutateAsync(targets[0].id)
        toast.success(t('manager.deleted'))
      } else {
        const res = await bulkDel.mutateAsync(targets.map((x) => x.id))
        toast.success(t('manager.deletedMany', { count: res.deleted }))
        if (res.forbidden) toast.warning(t('manager.forbiddenShared', { count: res.forbidden }))
      }
      done?.()
      setConfirm(null)
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const columns: TableColumn<QuickReply>[] = [
    {
      key: 'title',
      header: t('manager.columns.title'),
      cell: (q) => (
        <div className="min-w-[160px] max-w-[240px]">
          <p className="truncate font-medium" title={q.title}>
            {q.title}
          </p>
          {q.isPersonal && (
            <Badge variant="secondary" className="mt-0.5 font-normal">
              {t('manager.personal')}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'content',
      header: t('manager.columns.content'),
      cell: (q) => (
        <p className="line-clamp-2 min-w-[220px] max-w-[420px] whitespace-pre-line text-muted-foreground" title={q.content}>
          {q.content}
        </p>
      ),
    },
    {
      key: 'category',
      header: t('manager.columns.category'),
      className: 'whitespace-nowrap',
      cell: (q) =>
        q.category ? (
          <Badge variant="outline" className="max-w-[160px] truncate font-normal">
            {q.category}
          </Badge>
        ) : (
          <span className="text-muted-foreground">--</span>
        ),
    },
    {
      key: 'creator',
      header: t('manager.columns.creator'),
      cell: (q) =>
        q.createdBy ? (
          <span className="block max-w-[160px] truncate">{q.createdBy.fullName}</span>
        ) : (
          <span className="text-muted-foreground">--</span>
        ),
    },
    {
      key: 'updated',
      header: t('manager.columns.updated'),
      className: 'whitespace-nowrap',
      cell: (q) => dayjs(q.updatedAt).format('DD/MM/YYYY HH:mm'),
    },
    {
      key: 'actions',
      header: t('manager.columns.actions'),
      sticky: true,
      className: 'w-px whitespace-nowrap text-right',
      cell: (q) =>
        canEdit(q) || canDelete(q) ? (
          <div className="flex items-center justify-end gap-1">
            {canEdit(q) && (
              <Button variant="ghost" size="sm" onClick={() => setForm({ open: true, item: q })}>
                <Pencil /> {t('common:actions.edit')}
              </Button>
            )}
            {canDelete(q) && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => setConfirm({ items: [q] })}
                aria-label={t('manager.deleteAria', { title: q.title })}
              >
                <Trash2 />
              </Button>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground" title={t('manager.viewOnlyHint')}>
            {t('manager.viewOnly')}
          </span>
        ),
    },
  ]

  const chips: { value: string; label: string; count: number }[] = [
    { value: ALL, label: t('manager.all'), count: items.length },
    ...categories.map((c) => ({ value: c, label: c, count: counts.get(c) ?? 0 })),
    ...(counts.get(NONE) ? [{ value: NONE, label: t('manager.uncategorized'), count: counts.get(NONE)! }] : []),
  ]

  const deleting = del.isPending || bulkDel.isPending

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4 pr-12">
        <DialogHeader className="min-w-0 space-y-1">
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" /> {t('manager.title')}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('manager.description', { variable: '{{contact.firstName}}' })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <FileUp /> {t('manager.importCsv')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCsv(shown)} disabled={!shown.length}>
            <Download /> {t('manager.exportCsv')}
          </Button>
          <Button size="sm" onClick={() => setForm({ open: true, item: null })}>
            <Plus /> {t('manager.addReply')}
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {isLoading ? (
          <Loading label={t('manager.loading')} />
        ) : isError ? (
          <ErrorState message={apiError(error)} />
        ) : (
          <>
            {items.length > 0 && (
              <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('manager.filterByCategory')}>
                {chips.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    role="tab"
                    aria-selected={category === c.value}
                    onClick={() => setCategory(c.value)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors',
                      category === c.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    <span className="max-w-[160px] truncate">{c.label}</span>
                    <span className="tabular-nums opacity-70">{c.count}</span>
                  </button>
                ))}
              </div>
            )}

            <SelectableTable
              key={category}
              rows={shown}
              rowKey={(q) => q.id}
              columns={columns}
              itemLabel={t('manager.itemLabel')}
              searchPlaceholder={t('manager.searchPlaceholder')}
              searchText={(q) => `${q.title} ${q.content} ${q.category ?? ''} ${q.createdBy?.fullName ?? ''}`}
              bulkActions={(selected, clear) => (
                <>
                  <Button variant="outline" size="sm" onClick={() => exportCsv(selected)}>
                    <Download /> {t('manager.exportCsvCount', { count: selected.length })}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setConfirm({ items: selected, done: clear })}
                  >
                    <Trash2 /> {t('manager.deleteCount', { count: selected.length })}
                  </Button>
                </>
              )}
              empty={
                <EmptyState
                  icon={MessageSquareText}
                  title={items.length ? t('manager.emptyCategory') : t('manager.emptyLibrary')}
                  description={t('manager.emptyDescription')}
                  action={
                    <Button onClick={() => setForm({ open: true, item: null })}>
                      <Plus /> {t('manager.addReply')}
                    </Button>
                  }
                />
              }
            />
          </>
        )}
      </div>

      <QuickReplyFormDialog
        open={form.open}
        item={form.item}
        categories={categories}
        canManageTeam={can.create}
        defaultCategory={category === ALL || category === NONE ? null : category}
        onClose={() => setForm({ open: false, item: null })}
      />

      <QuickReplyImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        library={items}
        canManageTeam={can.import}
      />

      <Dialog open={!!confirm} onOpenChange={(v) => !v && !deleting && setConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirm?.items.length === 1
                ? t('manager.confirmTitleOne')
                : t('manager.confirmTitleMany', { count: confirm?.items.length })}
            </DialogTitle>
            <DialogDescription>
              {confirm?.items.length === 1 ? `"${confirm.items[0].title}" ` : ''}
              {t('manager.confirmDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={deleting}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="destructive" onClick={runDelete} disabled={deleting}>
              {deleting && <Loader2 className="animate-spin" />}
              {t('common:actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
